import React, { useEffect, useMemo, useState } from "react";
import styles from "./KeyboardPage.module.css";
import { FiX, FiDelete, FiArrowUpCircle } from "react-icons/fi";

const KeyboardPage = ({ open, value = "", onChange, onClose }) => {
  const [local, setLocal] = useState(value || "");
  const [lang, setLang] = useState("ru"); // ru | en | kz
  const [caps, setCaps] = useState(false);

  useEffect(() => {
    if (open) setLocal(value || "");
  }, [open, value]);

  useEffect(() => {
    if (!open) return;

    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  // ===== layouts =====
  const RU = useMemo(
    () => [
      ["Й", "Ц", "У", "К", "Е", "Н", "Г", "Ш", "Щ", "З", "Х", "Ъ"],
      ["Ф", "Ы", "В", "А", "П", "Р", "О", "Л", "Д", "Ж", "Э"],
      ["Я", "Ч", "С", "М", "И", "Т", "Ь", "Б", "Ю"],
    ],
    []
  );

  const EN = useMemo(
    () => [
      ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
      ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
      ["Z", "X", "C", "V", "B", "N", "M"],
    ],
    []
  );

  // KZ: казахские буквы добавлены (Ә, Ө, Ү, Ұ, Қ, Ғ, І, Ң, Һ)
  const KZ = useMemo(
    () => [
      ["Ә", "І", "Ң", "Ғ", "Ү", "Ұ", "Қ", "Ө", "Һ"],
      ["Й", "Ц", "У", "К", "Е", "Н", "Г", "Ш", "Щ", "З", "Х"],
      ["Ф", "Ы", "В", "А", "П", "Р", "О", "Л", "Д", "Ж", "Э"],
      ["Я", "Ч", "С", "М", "И", "Т", "Ь", "Б", "Ю"],
    ],
    []
  );

  const letters = useMemo(() => {
    if (lang === "en") return EN;
    if (lang === "kz") return KZ;
    return RU;
  }, [lang, EN, KZ, RU]);

  const digits = useMemo(
    () => [
      ["1", "2", "3"],
      ["4", "5", "6"],
      ["7", "8", "9"],
      [".", "0", "BACKSPACE"],
    ],
    []
  );

  if (!open) return null;

  const commit = (next) => {
    setLocal(next);
    onChange?.(next);
  };

  const isLetterChar = (char) => /[A-Za-zА-Яа-яӘәІіҢңҒғҮүҰұҚқӨөҺһ]/.test(char);

  const normalizeChar = (char) => {
    // цифры/символы не трогаем
    if (!isLetterChar(char)) return char;

    // caps=true => upper, caps=false => lower
    return caps ? char.toUpperCase() : char.toLowerCase();
  };

  // ✅ ВАЖНО: отображение на клавише меняется при caps
  const displayKey = (char) => {
    if (!isLetterChar(char)) return char;
    return caps ? char.toUpperCase() : char.toLowerCase();
  };

  const add = (char) => {
    const c = normalizeChar(char);
    commit(local + c);
  };

  const backspace = () => commit(local.slice(0, -1));
  const clear = () => commit("");

  const onDigitKey = (k) => {
    if (k === "BACKSPACE") return backspace();
    add(k);
  };

  return (
    <div className={styles.root}>
      <div className={styles.topRow}>
        <div className={styles.title}>КЛАВИАТУРА GREENMAG</div>

        <div className={styles.controls}>
          {/* языки */}
          <div className={styles.langTabs}>
            <button
              type="button"
              className={`${styles.langBtn} ${lang === "ru" ? styles.langActive : ""}`}
              onClick={() => setLang("ru")}
            >
              RU
            </button>
            <button
              type="button"
              className={`${styles.langBtn} ${lang === "en" ? styles.langActive : ""}`}
              onClick={() => setLang("en")}
            >
              EN
            </button>
            <button
              type="button"
              className={`${styles.langBtn} ${lang === "kz" ? styles.langActive : ""}`}
              onClick={() => setLang("kz")}
            >
              KZ
            </button>
          </div>

          <button
            className={styles.ctrlBtn}
            onClick={backspace}
            type="button"
            aria-label="Backspace"
            title="Backspace"
          >
            <FiDelete size={18} />
          </button>

          <button className={styles.ctrlBtn} onClick={clear} type="button" title="Очистить">
            C
          </button>

          <button className={styles.okBtn} onClick={onClose} type="button">
            OK
          </button>

          <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Закрыть">
            <FiX size={20} />
          </button>
        </div>
      </div>

      <div className={styles.keyboardGrid}>
        <div className={styles.left}>
          {letters.map((row, idx) => (
            <div className={styles.row} key={idx}>
              {row.map((k) => (
                <button key={k} type="button" className={styles.key} onClick={() => add(k)}>
                  {/* ✅ показываем регистр по caps */}
                  {displayKey(k)}
                </button>
              ))}
            </div>
          ))}

          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.key} ${styles.mid} ${caps ? styles.capsActiveKey : ""}`}
              onClick={() => setCaps((p) => !p)}
              title="Caps Lock"
              aria-label="Caps Lock"
            >
              <FiArrowUpCircle size={20} />
            </button>

            <button type="button" className={`${styles.key} ${styles.wide}`} onClick={() => add(" ")}>
              Пробел
            </button>

            <button type="button" className={`${styles.key} ${styles.mid}`} onClick={() => add("-")}>
              -
            </button>

            <button type="button" className={`${styles.key} ${styles.mid}`} onClick={() => add(".")}>
              .
            </button>
          </div>
        </div>

        <div className={styles.right}>
          {digits.map((row, idx) => (
            <div className={styles.numRow} key={idx}>
              {row.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`${styles.numKey} ${k === "BACKSPACE" ? styles.numBack : ""}`}
                  onClick={() => onDigitKey(k)}
                  aria-label={k === "BACKSPACE" ? "Backspace" : undefined}
                  title={k === "BACKSPACE" ? "Backspace" : undefined}
                >
                  {k === "BACKSPACE" ? <FiDelete size={20} /> : k}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KeyboardPage;
